import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { DndContext, closestCorners, PointerSensor, useSensor, useSensors, useDroppable, useDraggable } from "@dnd-kit/core";
import { User, Trophy, TrendingUp } from "lucide-react";

const STAGE_META = {
  lead:        { label: "Lead",        color: "bg-slate-400" },
  qualified:   { label: "Qualified",   color: "bg-sky-500" },
  proposal:    { label: "Proposal",    color: "bg-[#0A2540]" },
  negotiation: { label: "Negotiation", color: "bg-amber-500" },
  won:         { label: "Won",         color: "bg-emerald-600" },
  lost:        { label: "Lost",        color: "bg-red-500" },
};

export default function AdminSalesPipeline() {
  const [board, setBoard] = useState({});
  const [stages, setStages] = useState([]);
  const [repTotals, setRepTotals] = useState({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/pipeline");
      setBoard(r.data.board);
      setStages(r.data.stages);
      setRepTotals(r.data.rep_totals || {});
    } catch { toast.error("Failed to load pipeline"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const onDragEnd = async (event) => {
    const { active, over } = event;
    if (!over) return;
    const [estId] = String(active.id).split(":");
    const stage = String(over.id);
    const prev = board;
    const next = { ...board };
    let moving = null;
    for (const s of Object.keys(next)) {
      const idx = next[s].findIndex((e) => e.estimate_id === estId);
      if (idx > -1) { moving = next[s][idx]; next[s] = next[s].filter((_, i) => i !== idx); break; }
    }
    if (!moving || moving.pipeline_stage === stage) return;
    moving.pipeline_stage = stage;
    next[stage] = [moving, ...(next[stage] || [])];
    setBoard(next);
    try {
      await api.patch(`/estimates/${estId}/pipeline`, { stage });
      toast.success(`Moved to ${STAGE_META[stage]?.label || stage}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Move failed");
      setBoard(prev);
    }
  };

  const winRate = (r) => (!r || !r.value) ? 0 : Math.round((r.won_value / r.value) * 100);

  return (
    <div data-testid="admin-sales-pipeline">
      <p className="overline">Sales · CRM</p>
      <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2 mb-6">Sales Pipeline</h1>

      {Object.keys(repTotals).length > 0 && (
        <div className="card-pb p-5 mb-6" data-testid="rep-leaderboard">
          <div className="inline-flex items-center gap-2 mb-3">
            <Trophy className="w-4 h-4 text-[#C9A356]" />
            <p className="overline">Rep leaderboard (won revenue)</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(repTotals)
              .sort(([, a], [, b]) => b.won_value - a.won_value)
              .map(([rid, t]) => (
                <div key={rid} className="border border-[#E2E8F0] dark:border-slate-700 rounded p-3" data-testid={`rep-${rid}`}>
                  <div className="text-xs font-medium text-[#0A2540] dark:text-white inline-flex items-center gap-1">
                    <User className="w-3 h-3" /> {t.name || (rid === "_unassigned" ? "Unassigned" : rid.slice(0, 12))}
                  </div>
                  <div className="text-lg font-bold text-[#0A2540] dark:text-white mt-1 tabular-nums">${t.won_value.toLocaleString()}</div>
                  <div className="text-[10px] text-[#94A3B8]">{t.count} deals · {winRate(t)}% win</div>
                </div>
              ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-[#94A3B8]">Loading pipeline…</div>
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragEnd={onDragEnd}>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {stages.map((s) => <Column key={s} stage={s} deals={board[s] || []} />)}
          </div>
        </DndContext>
      )}
    </div>
  );
}

function Column({ stage, deals }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage });
  const meta = STAGE_META[stage] || { label: stage, color: "bg-slate-500" };
  const total = deals.reduce((s, d) => s + (d.grand_total || 0), 0);
  return (
    <div ref={setNodeRef} data-testid={`col-${stage}`}
         className={`rounded-lg p-3 min-h-[320px] transition ${isOver ? "bg-slate-100 dark:bg-slate-800 ring-2 ring-[#C9A356]" : "bg-slate-50 dark:bg-slate-900"}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="inline-flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${meta.color}`} />
          <span className="text-sm font-medium text-[#0A2540] dark:text-white">{meta.label}</span>
          <span className="text-xs text-[#94A3B8]">({deals.length})</span>
        </div>
        <div className="text-[10px] text-[#94A3B8] tabular-nums">${total.toLocaleString()}</div>
      </div>
      <div className="space-y-2">
        {deals.map((d) => <DealCard key={d.estimate_id} deal={d} />)}
      </div>
    </div>
  );
}

function DealCard({ deal }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: `${deal.estimate_id}:${deal.pipeline_stage}` });
  const style = transform ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, opacity: isDragging ? 0.6 : 1 } : {};
  return (
    <div ref={setNodeRef} {...attributes} {...listeners} style={style} data-testid={`deal-${deal.estimate_id}`}
         className="bg-white dark:bg-slate-800 border border-[#E2E8F0] dark:border-slate-700 rounded-md p-3 cursor-grab active:cursor-grabbing hover:border-[#C9A356] hover:shadow-sm transition">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm font-medium text-[#0A2540] dark:text-white truncate">{deal.title}</div>
          <div className="text-[11px] text-[#475569] dark:text-slate-400 truncate">{deal.client_name}</div>
        </div>
        <Link to={`/admin/estimates/${deal.estimate_id}`}
              onClick={(e) => e.stopPropagation()}
              onPointerDown={(e) => e.stopPropagation()}
              className="text-[10px] text-[#C9A356] hover:underline shrink-0">Open</Link>
      </div>
      <div className="flex items-center justify-between mt-2 text-[11px]">
        <span className="text-[#94A3B8] font-mono">{deal.estimate_id}</span>
        <span className="text-[#0A2540] dark:text-white font-bold tabular-nums">${(deal.grand_total || 0).toLocaleString()}</span>
      </div>
      {deal.owner_rep_name && (
        <div className="text-[10px] text-[#94A3B8] mt-1 inline-flex items-center gap-1">
          <User className="w-2.5 h-2.5" /> {deal.owner_rep_name}
        </div>
      )}
      {deal.discount_approval_status === "pending" && (
        <div className="text-[9px] uppercase font-bold text-amber-700 bg-amber-100 dark:bg-amber-900/30 px-1.5 py-0.5 rounded mt-2 inline-block">
          Discount pending
        </div>
      )}
      {deal.discount_code?.code && (
        <div className="text-[9px] uppercase font-bold text-[#8A6B14] bg-[#FFFBF0] px-1.5 py-0.5 rounded mt-1 inline-block">
          {deal.discount_code.code} · -{deal.discount_code.discount_pct}%
        </div>
      )}
    </div>
  );
}
